// ACH-host detection and Bearer-token resolution for the fetch chokepoint.
//
// The chokepoint (services.tsx) attaches `Authorization: Bearer <token>` iff the
// target host is an ACH host — never to Ventura/Rails hosts (which authenticate
// by Devise session cookie). Host identity is decided by membership in the
// checked-in achApiHostMap, so a request can only ever gain the Bearer for a
// known ACH host, never for Rails.
//
// NOTE: the token resolution here intentionally mirrors src/api/ach/client.ts
// (the Tier-A achGetJSON client). The two are kept independent on purpose — the
// Tier-A client is out of scope to touch/converge — so this is a deliberate,
// small duplication rather than a shared refactor.

import { ACH_PROXY_PREFIX } from '../constants/ach/proxy';
import { achApiHostMap } from '../constants/config';

// The dev-server ACH proxy prefix counts as an ACH host: in local dev the app
// targets ACH through it (getDefaultAchApiHost => ACH_PROXY_PREFIX) to sidestep
// CORS, and the fetch chokepoint must still recognize it to apply ACH path
// rewriting / Bearer / envelope handling. It is same-origin, so no Bearer leaks
// cross-origin; the proxy re-attaches the token server-side anyway.
const ACH_HOSTS: ReadonlySet<string> = new Set([...Object.values(achApiHostMap), ACH_PROXY_PREFIX]);

// isAchHost is true for a host that exactly matches a checked-in ACH host (or the
// dev proxy prefix above). A localStorage-overridden custom ACH host would not
// match — Bearer is then omitted (degraded, but safe: the token is never sent to
// a non-ACH origin).
export const isAchHost = (host: string): boolean => ACH_HOSTS.has(host);

// achBearerToken resolves the ACH Bearer token: the logged-in user's _up-session
// SSO token (sso_token) from the localStorage 'user' blob. jwt_token (the Ventura
// X-Token) is NOT an RS256 _up-session JWT and is rejected by ACH, so it is not
// used. A DEV-only VITE_ACH_UP_SESSION overrides it for local testing.
export const achBearerToken = (): string | undefined => {
  const devToken = import.meta.env.DEV ? import.meta.env.VITE_ACH_UP_SESSION : undefined;
  if (devToken) return devToken;
  try {
    const raw = localStorage.getItem('user');
    return (raw ? JSON.parse(raw)?.sso_token : undefined) || undefined;
  } catch {
    return undefined;
  }
};

// --- ACH-2423 stopgap: one-shot silent re-auth on ACH 401 --------------------
// When a direct-to-ACH call 401s AND an sso_token WAS attached, the token has
// gone stale while the Hub SSO session is (normally) still live — Ventura echoes
// its OmniAuth access_token once and never refreshes it. Manual logout/login is
// proven to fix it; this reproduces that automatically by signing the user out
// of Ventura FIRST, then bouncing ONCE through the existing SSO flow (mirrors
// useAuth.ssoLogin), which lands on /sso/auth and re-captures a fresh
// X-Token -> sso_token, then returns the user to their exact page via a return_to
// deep link. The sign-out is required: Ventura's add_headers only re-captures a
// fresh OmniAuth access_token when the session has NO token — with a live session
// it echoes the existing (dead) one, so re-authing into the still-alive session
// is a no-op (verified on QA). Destroying the session first forces the
// fresh-capture branch. The Hub SSO cookie is a separate cookie and survives, so
// the flow completes with no login form. A sessionStorage guard caps this to one
// auto-bounce per window, so a token ACH keeps rejecting falls through to today's
// error UI instead of looping. Durable fix (ACH-side silent refresh) is deferred:
// bean ACH-rfa8.
const REAUTH_GUARD_KEY = 'achReauthBounceAt';
export const REAUTH_GUARD_WINDOW_MS = 5 * 60 * 1000;

// shouldReauthOnAchUnauthorized is the pure decision: bounce only on a 401 with a
// token attached (absent => password-login degraded mode, unchanged) and only
// when outside the guard window (no recent bounce => no loop).
export const shouldReauthOnAchUnauthorized = (
  status: number,
  hadToken: boolean,
  now: number,
  lastBounceAt: number | null
): boolean => {
  if (status !== 401 || !hadToken) return false;
  if (lastBounceAt !== null && now - lastBounceAt < REAUTH_GUARD_WINDOW_MS) return false;
  return true;
};

const readApiHost = (): string | undefined => {
  try {
    const raw = localStorage.getItem('apiHost');
    return (raw ? JSON.parse(raw) : undefined) || undefined;
  } catch {
    return undefined;
  }
};

// ssoReauthUrl builds the Ventura SSO entry URL that round-trips the user back to
// their exact page. `origin` carries a return_to (path+query+hash) the /sso/auth
// landing reads, so it is URL-encoded as a whole (it now has its own query).
export const ssoReauthUrl = (apiHost: string, currentHost: string, returnTo: string): string => {
  const suffix = import.meta.env.VITE_IS_FEDERATED === 'true' ? '/onedsp' : '';
  const origin = `//${currentHost}${suffix}/sso/auth?return_to=${encodeURIComponent(returnTo)}`;
  return `${apiHost}/d_/users/auth/ninthdecimal?origin=${encodeURIComponent(origin)}`;
};

// reauthReturnTo is the current SPA location (path+query+hash) with the federated
// basename stripped, so react-router's navigate (which prepends the basename)
// lands the user back exactly where they were.
const reauthReturnTo = (): string => {
  const base = import.meta.env.VITE_IS_FEDERATED === 'true' ? '/onedsp' : '';
  let path = window.location.pathname || '/';
  if (base && (path === base || path.startsWith(`${base}/`))) {
    path = path.slice(base.length) || '/';
  }
  return path + window.location.search + window.location.hash;
};

// venturaSignOut destroys the live Ventura Devise session using the EXACT backend
// call the logout button issues (useAuth.logout): DELETE /d_/users/sign_out with
// the session cookie. Only the backend call is reused — NOT logout's FE-state
// reset (clearing localStorage 'user' / auth state), because the /sso/auth landing
// repopulates those after the round-trip.
const venturaSignOut = async (apiHost: string): Promise<void> => {
  const headers = new Headers();
  headers.append('Accept', 'application/json');
  headers.append('Accept-Language', 'en-US,en;q=0.9');
  headers.append('Cache-Control', 'no-cache');
  headers.append('Content-Type', 'application/x-www-form-urlencoded');
  await fetch(`${apiHost}/d_/users/sign_out`, {
    method: 'DELETE',
    headers,
    redirect: 'follow',
    credentials: 'include',
  });
};

// maybeReauthOnAchUnauthorized is the shared handler both ACH call paths invoke on
// a 401. Resolves true iff it initiated an auto-reauth bounce (the caller must then
// stop — the page is navigating away); false to fall through to today's error
// handling (no token, inside the guard window, or no resolvable Ventura host).
export const maybeReauthOnAchUnauthorized = async (status: number, hadToken: boolean): Promise<boolean> => {
  let lastBounceAt: number | null = null;
  try {
    const raw = sessionStorage.getItem(REAUTH_GUARD_KEY);
    const n = raw ? Number(raw) : NaN;
    lastBounceAt = Number.isFinite(n) ? n : null;
  } catch {
    lastBounceAt = null;
  }

  const now = Date.now();
  if (!shouldReauthOnAchUnauthorized(status, hadToken, now, lastBounceAt)) return false;

  const apiHost = readApiHost();
  if (!apiHost) return false;

  try {
    // Arm the guard synchronously BEFORE any async step so concurrent 401s (and
    // the post-bounce page load) see it and cannot re-bounce within the window.
    sessionStorage.setItem(REAUTH_GUARD_KEY, String(now));
  } catch {
    return false;
  }

  // Sign out of Ventura FIRST and wait for it to COMPLETE before navigating: the
  // fresh-token capture only happens when the session has no token, and a late
  // sign-out racing the new SSO session would destroy the fresh login. If sign-out
  // fails (network error / already-dead session) still proceed — worst case the
  // SSO flow does a full login, strictly better than staying wedged.
  try {
    await venturaSignOut(apiHost);
  } catch {
    // Intentionally ignored — navigate anyway (see above).
  }

  window.location.href = ssoReauthUrl(apiHost, window.location.host, reauthReturnTo());
  return true;
};
